import React, { useEffect, useState, useRef } from 'react';
import { useHospitalSelection } from '../../hooks/useHospitalSelection';
import CheckIcon from '../../components/Common/CheckIcon';
import CrossIcon from '../../components/Common/CrossIcon';
import MultiSelectNotice from '../../components/Common/MultiSelectNotice';
import NoDataOverlay from '../../components/Common/NoDataOverlay';
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice';
import { exportToPNG, exportToSVG } from '../../utils/exportUtils';
import { generatePDFIds } from '../../utils/pdfIdHelper';
import { parseMonthToValue, expandMonthName } from '../../utils/dataUtils';
import './EngagementMeasures.css';

const EngagementMeasures: React.FC = () => {
    const { selectedHospitalIds, isMultiSelect, refreshKey } = useHospitalSelection();
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [hasData, setHasData] = useState<boolean>(true);
    const exportRef = useRef<HTMLDivElement>(null);
    const printExportRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (!(e.target as Element).closest('.export-menu')) {
                const menu = document.getElementById('export-dropdown-menu-engagement')
                if (menu) menu.classList.remove('show')
            }
        }
        document.addEventListener('click', handleClickOutside)
        return () => document.removeEventListener('click', handleClickOutside)
    }, [])

    useEffect(() => {
        if (isMultiSelect || selectedHospitalIds.length === 0) {
            setData(null);
            setHasData(false);
            setLoading(false);
            return;
        }

        const hospitalId = selectedHospitalIds[0];
        setLoading(true);

        fetch(`/api/engagement_measures_y2/${hospitalId}`)
            .then(res => res.json())
            .then(result => {
                const rows = result?.response?.result?.rows || [];
                const hospitalRows = rows.filter((r: any) => r[0] === hospitalId);
                let row = null;
                if (hospitalRows.length > 1) {
                    hospitalRows.sort((a: any, b: any) => {
                        return parseMonthToValue(b[3]) - parseMonthToValue(a[3]);
                    });
                    row = hospitalRows[0];
                } else {
                    row = hospitalRows[0] || null;
                }
                setData(row);
                setHasData(!!row);
                setLoading(false);
            })
            .catch(err => {
                console.error("Error fetching engagement measures:", err);
                setData(null);
                setHasData(false);
                setLoading(false);
            });
    }, [selectedHospitalIds, isMultiSelect, refreshKey]);

    const renderQualityIcon = (value: any) => {
        if (String(value) === "Check") {
            return <CheckIcon />;
        }
        return <CrossIcon />;
    };

    const handleDownload = async () => {
        if (printExportRef.current) {
            await exportToPNG(printExportRef.current, 'engagement_measures', undefined, {
                forceDesktopCapture: true
            });
        }
    };

    const handleDownloadSVG = async () => {
        if (printExportRef.current) {
            await exportToSVG(printExportRef.current, 'engagement_measures', undefined, {
                forceDesktopCapture: true
            });
        }
    };

    if (loading) {
        return <LoadingNotice title="Loading engagement measures data..." />;
    }

    const showNoDataOverlay = !hasData && !loading && !isMultiSelect;

    const rawMonth = data?.[3] || "";
    const reportingMonth = rawMonth ? expandMonthName(rawMonth) : "NA";
    const webinarsProgress = data?.[11] || "N/A";

    const cumulativeSubmissions = data?.[6] || "N/A";
    const respectfulCareProgress = data?.[9] || "N/A";
    const qiSupportCount = data?.[14] || "N/A";

    const meetingNurse = data?.[20] || "N/A";
    const meetingProvider = data?.[21] || "N/A";
    const annualNurse = data?.[25] || "N/A";
    const annualProvider = data?.[24] || "N/A";

    const quality1 = data?.[13];
    const quality3 = data?.[15];
    const quality4 = data?.[7];
    const quality5 = data?.[8];
    const quality6 = data?.[10];

    return (
        <div className="engagement-measures-page" data-export-target={generatePDFIds.exportTarget('engagement-measures')} style={isMultiSelect ? { overflow: 'hidden', maxHeight: 'calc(100vh - 80px)' } : undefined}>
            <MultiSelectNotice visible={isMultiSelect} />
            <NoDataOverlay visible={showNoDataOverlay} />
            <div ref={exportRef}>
                <div className="report-header">
                    <div>
                        <h1 className="page-title">Engagement Measures</h1>
                        <p className="reporting-month">Reporting Month: {reportingMonth || "NA"}</p>
                    </div>
                </div>

                {!showNoDataOverlay && (
                    <div className="report-container">
                        <div className="export-menu">
                            <button className="hamburger-btn" onClick={(e) => {
                                e.stopPropagation()
                                const menu = document.getElementById('export-dropdown-menu-engagement')
                                if (menu) menu.classList.toggle('show')
                            }}>&#9776;</button>
                            <div id="export-dropdown-menu-engagement" className="export-dropdown-menu">
                                <div onClick={() => {
                                    const menu = document.getElementById('export-dropdown-menu-engagement')
                                    if (menu) menu.classList.remove('show')
                                    handleDownload()
                                }}>Download PNG</div>
                                <div onClick={() => {
                                    const menu = document.getElementById('export-dropdown-menu-engagement')
                                    if (menu) menu.classList.remove('show')
                                    handleDownloadSVG()
                                }}>Download SVG</div>
                            </div>
                        </div>

                        <div className="table-wrapper">
                            <table id={generatePDFIds.table('engagement')} className="measures-table">
                        <thead>
                            <tr>
                                <th></th>
                                <th>Measures</th>
                                <th colSpan={2}>
                                    <div className="progress-quality-header">
                                        <span>Progress</span>
                                        <span>Quality<br />Excellence</span>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Webinars</td>
                                <td>
                                    <div>Attendance at 10 or more monthly team webinars</div>
                                    <br />
                                </td>
                                <td colSpan={2}>
                                    <div className="progress-quality-grid">
                                        <div className="progress-item">No. of monthly webinars attended: {webinarsProgress}</div>
                                        <div className="quality-item">{renderQualityIcon(quality1)}</div>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td>Meetings</td>
                                <td>
                                    <div>Attendance of nurse champion at 2026 ILPQC face-to-face meeting and annual conference.</div>
                                    <div style={{ marginTop: '8px' }}>Attendance of provider champion at 2026 ILPQC face-to-face meeting and/or annual conference</div>
                                </td>
                                <td colSpan={2}>
                                    <div className="progress-quality-grid">
                                        <div className="progress-item">ILPQC face-to-face nurse attendee</div>
                                        <div className="quality-item">{renderQualityIcon(meetingNurse)}</div>
                                        
                                        <div className="progress-item">ILPQC face-to-face provider attendee</div>
                                        <div className="quality-item">{renderQualityIcon(meetingProvider)}</div>
                                        
                                        <div className="progress-item">ILPQC annual conference nurse attendee</div>
                                        <div className="quality-item">{renderQualityIcon(annualNurse)}</div>
                                        
                                        <div className="progress-item">ILPQC annual conference provider attendee</div>
                                        <div className="quality-item">{renderQualityIcon(annualProvider)}</div>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td>Participation in<br />QI Support</td>
                                <td>Attend 2 or more quality improvement support contacts with ILPQC per year. Includes office hours and at least one 1:1 support call (includes PMH initiative)</td>
                                <td colSpan={2}>
                                    <div className="progress-quality-grid">
                                        <div className="progress-item">No. of QI support attended: {qiSupportCount}</div>
                                        <div className="quality-item">{renderQualityIcon(quality3)}</div>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td>Data<br />Submissions</td>
                                <td>12 months of monthly data from Sep 2025 - Aug 2026</td>
                                <td colSpan={2}>
                                    <div className="progress-quality-grid">
                                        <div className="progress-item">No. of cumulative data submissions in the last year (starting Sep 2025): {cumulativeSubmissions}</div>
                                        <div className="quality-item">{renderQualityIcon(quality4)}</div>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td>Respectful Care<br />Breakfasts</td>
                                <td>Hold 2 or more respectful care breakfasts per year</td>
                                <td colSpan={2}>
                                    <div className="progress-quality-grid">
                                        <div className="progress-item">No. of cumulative respectful care breakfasts in the last year (starting Sep 2025): {respectfulCareProgress}</div>
                                        <div className="quality-item">{renderQualityIcon(quality6)}</div>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td>Patient Partner</td>
                                <td>Engagement of a patient partner in the quality improvement team</td>
                                <td colSpan={2}>
                                    <div className="progress-quality-grid">
                                        <div className="progress-item"></div>
                                        <div className="quality-item">{renderQualityIcon(quality5)}</div>
                                    </div>
                                </td>
                            </tr>
                            </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {!showNoDataOverlay && !isMultiSelect && (
                <div ref={printExportRef} className="export-only-container" style={{ position: 'absolute', left: '-9999px', top: 0, width: '1200px', background: '#f0f6fa', padding: '1rem' }}>
                    <div className="report-header">
                        <h1 className="page-title">Engagement Measures</h1>
                        <p className="reporting-month">Reporting Month: {reportingMonth || "NA"}</p>
                    </div>
                    <div className="report-container" style={{ background: 'white', borderRadius: '8px', padding: '1rem' }}>
                        <table className="measures-table export-table" style={{ borderCollapse: 'separate', borderSpacing: 0, border: '1px solid #ccc', width: '100%' }}>
                            <thead>
                                <tr>
                                    <th style={{ border: '1px solid #ccc', borderBottom: '2px solid #1C498B', backgroundColor: '#e8f4fc', padding: '12px 10px', fontWeight: 700, width: '10%' }}></th>
                                    <th style={{ border: '1px solid #ccc', borderBottom: '2px solid #1C498B', backgroundColor: '#e8f4fc', padding: '12px 10px', fontWeight: 700, width: '40%' }}>Measures</th>
                                    <th colSpan={2} style={{ border: '1px solid #ccc', borderBottom: '2px solid #1C498B', backgroundColor: '#e8f4fc', padding: 0, width: '50%' }}>
                                        <table className="nested-align-table header-table" style={{ width: '100%', border: 'none', borderCollapse: 'collapse' }}>
                                            <colgroup>
                                                <col style={{ width: '80%' }} />
                                                <col style={{ width: '20%' }} />
                                            </colgroup>
                                            <tbody>
                                                <tr>
                                                    <td style={{ fontWeight: 700, padding: '12px 10px', border: 'none' }}>Progress</td>
                                                    <td style={{ fontWeight: 700, textAlign: 'center', padding: '12px 10px', border: 'none', borderLeft: '1px solid #ccc' }}>Quality<br />Excellence</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={{ border: '1px solid #ccc', padding: '12px 10px', fontWeight: 500 }}>Webinars</td>
                                    <td style={{ border: '1px solid #ccc', padding: '12px 10px' }}>
                                        <div>Attendance at 10 or more monthly team webinars</div>
                                        <br />
                                    </td>
                                    <td colSpan={2} style={{ border: '1px solid #ccc', padding: 0 }}>
                                        <table className="nested-align-table" style={{ width: '100%', border: 'none', borderCollapse: 'collapse' }}>
                                            <colgroup>
                                                <col style={{ width: '80%' }} />
                                                <col style={{ width: '20%' }} />
                                            </colgroup>
                                            <tbody>
                                                <tr>
                                                    <td style={{ padding: '12px 10px', border: 'none' }}>No. of monthly webinars attended: {webinarsProgress}</td>
                                                    <td style={{ padding: '12px 10px', border: 'none', borderLeft: '1px solid #ccc', textAlign: 'center' }}>{renderQualityIcon(quality1)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </td>
                                </tr>
                                <tr style={{ backgroundColor: '#f5f9fc' }}>
                                    <td style={{ border: '1px solid #ccc', padding: '12px 10px', fontWeight: 500 }}>Meetings</td>
                                    <td style={{ border: '1px solid #ccc', padding: '12px 10px' }}>
                                        <div>Attendance of nurse champion at 2026 ILPQC face-to-face meeting and annual conference.</div>
                                        <div style={{ marginTop: '8px' }}>Attendance of provider champion at 2026 ILPQC face-to-face meeting and/or annual conference</div>
                                    </td>
                                    <td colSpan={2} style={{ border: '1px solid #ccc', padding: 0 }}>
                                        <table className="nested-align-table" style={{ width: '100%', border: 'none', borderCollapse: 'collapse' }}>
                                            <colgroup>
                                                <col style={{ width: '80%' }} />
                                                <col style={{ width: '20%' }} />
                                            </colgroup>
                                            <tbody>
                                                <tr>
                                                    <td style={{ padding: '8px 10px', border: 'none' }}>ILPQC face-to-face nurse attendee</td>
                                                    <td style={{ padding: '8px 10px', border: 'none', borderLeft: '1px solid #ccc', textAlign: 'center' }}>{renderQualityIcon(meetingNurse)}</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: '8px 10px', border: 'none' }}>ILPQC face-to-face provider attendee</td>
                                                    <td style={{ padding: '8px 10px', border: 'none', borderLeft: '1px solid #ccc', textAlign: 'center' }}>{renderQualityIcon(meetingProvider)}</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: '8px 10px', border: 'none' }}>ILPQC annual conference nurse attendee</td>
                                                    <td style={{ padding: '8px 10px', border: 'none', borderLeft: '1px solid #ccc', textAlign: 'center' }}>{renderQualityIcon(annualNurse)}</td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: '8px 10px', border: 'none' }}>ILPQC annual conference provider attendee</td>
                                                    <td style={{ padding: '8px 10px', border: 'none', borderLeft: '1px solid #ccc', textAlign: 'center' }}>{renderQualityIcon(annualProvider)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ccc', padding: '12px 10px', fontWeight: 500 }}>Participation in<br />QI Support</td>
                                    <td style={{ border: '1px solid #ccc', padding: '12px 10px' }}>Attend 2 or more quality improvement support contacts with ILPQC per year. Includes office hours and at least one 1:1 support call (includes PMH initiative)</td>
                                    <td colSpan={2} style={{ border: '1px solid #ccc', padding: 0 }}>
                                        <table className="nested-align-table" style={{ width: '100%', border: 'none', borderCollapse: 'collapse' }}>
                                            <colgroup>
                                                <col style={{ width: '80%' }} />
                                                <col style={{ width: '20%' }} />
                                            </colgroup>
                                            <tbody>
                                                <tr>
                                                    <td style={{ padding: '12px 10px', border: 'none' }}>No. of QI support attended: {qiSupportCount}</td>
                                                    <td style={{ padding: '12px 10px', border: 'none', borderLeft: '1px solid #ccc', textAlign: 'center' }}>{renderQualityIcon(quality3)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </td>
                                </tr>
                                <tr style={{ backgroundColor: '#f5f9fc' }}>
                                    <td style={{ border: '1px solid #ccc', padding: '12px 10px', fontWeight: 500 }}>Data<br />Submissions</td>
                                    <td style={{ border: '1px solid #ccc', padding: '12px 10px' }}>12 months of monthly data from Sep 2025 - Aug 2026</td>
                                    <td colSpan={2} style={{ border: '1px solid #ccc', padding: 0 }}>
                                        <table className="nested-align-table" style={{ width: '100%', border: 'none', borderCollapse: 'collapse' }}>
                                            <colgroup>
                                                <col style={{ width: '80%' }} />
                                                <col style={{ width: '20%' }} />
                                            </colgroup>
                                            <tbody>
                                                <tr>
                                                    <td style={{ padding: '12px 10px', border: 'none' }}>No. of cumulative data submissions in the last year (starting Sep 2025): {cumulativeSubmissions}</td>
                                                    <td style={{ padding: '12px 10px', border: 'none', borderLeft: '1px solid #ccc', textAlign: 'center' }}>{renderQualityIcon(quality4)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ border: '1px solid #ccc', padding: '12px 10px', fontWeight: 500 }}>Respectful Care<br />Breakfasts</td>
                                    <td style={{ border: '1px solid #ccc', padding: '12px 10px' }}>Hold 2 or more respectful care breakfasts per year</td>
                                    <td colSpan={2} style={{ border: '1px solid #ccc', padding: 0 }}>
                                        <table className="nested-align-table" style={{ width: '100%', border: 'none', borderCollapse: 'collapse' }}>
                                            <colgroup>
                                                <col style={{ width: '80%' }} />
                                                <col style={{ width: '20%' }} />
                                            </colgroup>
                                            <tbody>
                                                <tr>
                                                    <td style={{ padding: '12px 10px', border: 'none' }}>No. of cumulative respectful care breakfasts in the last year (starting Sep 2025): {respectfulCareProgress}</td>
                                                    <td style={{ padding: '12px 10px', border: 'none', borderLeft: '1px solid #ccc', textAlign: 'center' }}>{renderQualityIcon(quality6)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </td>
                                </tr>
                                <tr style={{ backgroundColor: '#f5f9fc' }}>
                                    <td style={{ border: '1px solid #ccc', padding: '12px 10px', fontWeight: 500 }}>Patient Partner</td>
                                    <td style={{ border: '1px solid #ccc', padding: '12px 10px' }}>Engagement of a patient partner in the quality improvement team</td>
                                    <td colSpan={2} style={{ border: '1px solid #ccc', padding: 0 }}>
                                        <table className="nested-align-table" style={{ width: '100%', border: 'none', borderCollapse: 'collapse' }}>
                                            <colgroup>
                                                <col style={{ width: '80%' }} />
                                                <col style={{ width: '20%' }} />
                                            </colgroup>
                                            <tbody>
                                                <tr>
                                                    <td style={{ padding: '12px 10px', border: 'none' }}>&nbsp;</td>
                                                    <td style={{ padding: '12px 10px', border: 'none', borderLeft: '1px solid #ccc', textAlign: 'center' }}>{renderQualityIcon(quality5)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EngagementMeasures;
